import React, { useEffect, useState } from 'react';
import { all_routes } from '../../router/all_routes';
import { Link, useNavigate } from 'react-router-dom';
import ImageWithBasePath from '../../../core/common/imageWithBasePath';
import { useLocation } from 'react-router-dom';
import API_URLS from '../../../core/common/apiUrls';
import AirplaneLoader from '../flight-loader/flightLoader';

interface Baggage {
    AirlineCode: string;
    Code: string;
    Currency: string;
    Description: number;
    Destination: string;
    FlightNumber: string;
    Origin: string;
    Price: number;
    WayType: number;
    Weight: number;
}

interface MealDynamic {
    AirlineCode: string;
    AirlineDescription: string;
    Code: string;
    Currency: string;
    Description: number;
    Destination: string;
    FlightNumber: string;
    Origin: string;
    Price: number;
    Quantity: number;
    WayType: number;
}

interface SeatDynamic {
    AirlineCode: string;
    AvailabilityType: number;
    Code: string;
    Compartment: number;
    CraftType: string;
    Currency: string;
    Deck: number;
    Description: number;
    Destination: string;
    FlightNumber: string;
    Origin: string;
    Price: number;
    RowNo: string;
    SeatNo: string | null;
    SeatType: number;
    SeatWayType: number;
}

interface Fare {
    AdditionalTxnFeeOfrd: number;
    AdditionalTxnFeePub: number;
    BaseFare: number;
    OtherCharges: number;
    Tax: number;
    YQTax: number;
}

interface Passenger {
    AddressLine1: string;
    AddressLine2: string;
    Baggage: Baggage[];
    City: string;
    ContactNo: string;
    CountryCode: string;
    CountryName: string;
    DateOfBirth: string;
    Email: string;
    FFAirlineCode: string | null;
    FFNumber: string | null;
    Fare: Fare;
    FirstName: string;
    GSTCompanyAddress: string;
    GSTCompanyContactNumber: string;
    GSTCompanyEmail: string;
    GSTCompanyName: string;
    GSTNumber: string;
    Gender: number;
    IsLeadPax: boolean;
    LastName: string;
    MealDynamic: MealDynamic[];
    Nationality: string;
    PassportExpiry: string | null;
    PassportNo: string;
    PaxType: number;
    SeatDynamic: SeatDynamic[];
    TicketId: number;
    Title: string;
}

interface LeadPassenger {
    email: string;
    first_name: string;
    last_name: string;
    phone: string;
    title: string;
}

interface BookingId {
    booking_id: number;
    flight_offer_id: number;
    pnr: string;
    result_index: string;
    status: string;
    trip_type: string;
}

interface BookingConfirmationData {
    booking_ids: BookingId[];
    email_sent: boolean;
    lead_passenger: LeadPassenger;
    passengers: Passenger[];
    pdf_base64: string;
    pdf_path: string;
}

interface BookingConfirmationResponse {
    code: number;
    data: BookingConfirmationData;
    message: string;
}

const FlightBookingConfirmation = () => {
    const routes = all_routes;
    const location = useLocation();
    const navigate = useNavigate();
    const [bookingData, setBookingData] = useState<BookingConfirmationData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        window.scrollTo(0, 0);

        // Arriving from hold completion — data already in state, no API call needed
        if (location.state?.fromHoldComplete && location.state?.completedData) {
            setBookingData(location.state.completedData);
            setLoading(false);
            return;
        }

        const confirmBooking = async () => {
            try {
                const { payment_id, temp_id, isHoldMode } = location.state || {};

                const url = isHoldMode
                    ? API_URLS.FLIGHTS.HOLD_FLIGHT
                    : API_URLS.FLIGHTS.CONFIRM_BOOKING;

                const response = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ temp_id, payment_id }),
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data: BookingConfirmationResponse = await response.json();

                if (data.code !== 200) {
                    throw new Error(data.message || "Booking confirmation failed");
                }

                if (isHoldMode) {
                    navigate(routes.flightHoldConfirmation, {
                        state: {
                            holdData: data.data,
                            totalFare: location.state?.totalFare,
                            holdDepositAmount: location.state?.holdDepositAmount,
                        },
                        replace: true,
                    });
                    return;
                }

                setBookingData(data.data);
                setLoading(false);
            } catch (err: any) {
                setError(err.message || "Failed to confirm booking");
                setLoading(false);
            }
        };

        confirmBooking();
    }, [location.state]);

    const getPassengerType = (paxType: number) => {
        switch (paxType) {
            case 1: return "Adult";
            case 2: return "Child";
            case 3: return "Infant";
            default: return "Passenger";
        }
    };

    const getGenderText = (gender: number) => {
        return gender === 1 ? "Male" : "Female";
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getTripTypeDisplay = (tripType: string) => {
        switch (tripType) {
            case 'roundtrip': return 'Round Trip';
            case 'oneway': return 'One Way';
            case 'multicity': return 'Multi City';
            default: return tripType;
        }
    };

    const getStatusBadgeClass = (status: string) => {
        switch (status.toLowerCase()) {
            case 'ticketed': return 'badge-success';
            case 'confirmed': return 'badge-primary';
            case 'pending': return 'badge-warning';
            case 'cancelled': return 'badge-danger';
            default: return 'badge-secondary';
        }
    };

    // Calculate totals from passenger fares
    const totalBaseFare = bookingData?.passengers.reduce((sum, passenger) => sum + passenger.Fare.BaseFare, 0) || 0;
    const totalTax = bookingData?.passengers.reduce((sum, passenger) => sum + passenger.Fare.Tax, 0) || 0;
    const totalAmount = totalBaseFare + totalTax;

    // Group passengers by ticket/booking segment
    const getPassengersByBooking = (bookingId: number) => {
        return bookingData?.passengers.filter(passenger => {
            // This logic might need adjustment based on how passengers are linked to bookings
            // For now, return all passengers for the first booking ID
            return bookingData.booking_ids.some(booking =>
                booking.booking_id === bookingId
            );
        }) || [];
    };

    if (loading) {
        return (
            <div>
                <div className={`breadcrumb-bar flightList-banner-bg text-center`}>
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12 col-12">
                                <h2 className="breadcrumb-title mb-2">Booking Confirmation</h2>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flight-loading">
                    <AirplaneLoader text="Confirming your booking..." size="large" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <div className={`breadcrumb-bar flightList-banner-bg text-center`}>
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12 col-12">
                                <h2 className="breadcrumb-title mb-2">Booking Confirmation</h2>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flight-error">
                    <div className="error-content">
                        <i className="isax isax-close-circle"></i>
                        <h4>Something went wrong</h4>
                        <p>{error}</p>
                        <button
                            className="btn btn-primary mt-3"
                            onClick={() => navigate(routes.home1)}
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className={`breadcrumb-bar flightList-banner-bg text-center`}>
                <div className="container">
                    <div className="row">
                        <div className="col-md-12 col-12">
                            <h2 className="breadcrumb-title mb-2">Booking Confirmation</h2>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-10">
                            <div className="card booking-confirmation mb-0">
                                <div className="card-body">
                                    {/* Success Header */}
                                    <div className="text-center mb-4">
                                        <div className="bg-success rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                            style={{ width: '80px', height: '80px' }}>
                                            <i className="isax isax-tick-circle text-white" style={{ fontSize: '40px' }}></i>
                                        </div>
                                        <h3 className="text-success mb-2">Booking Confirmed!</h3>
                                        <p className="text-muted">Your flight has been successfully booked</p>

                                        {/* Display all PNRs */}
                                        {bookingData?.booking_ids && bookingData.booking_ids.length > 0 && (
                                            <div className="d-flex flex-wrap justify-content-center gap-2 mt-3">
                                                {bookingData.booking_ids.map((booking, index) => (
                                                    <div key={booking.booking_id} className="bg-light p-3 rounded">
                                                        <div>
                                                            <strong>PNR {bookingData.booking_ids.length > 1 ? index + 1 : ''}: </strong>
                                                            <span className="text-primary fw-bold">{booking.pnr}</span>
                                                        </div>
                                                        <div className="mt-1 small">
                                                            <span className="badge badge-sm me-2 {getStatusBadgeClass(booking.status)}">
                                                                {booking.status}
                                                            </span>
                                                            <span className="text-muted">
                                                                {getTripTypeDisplay(booking.trip_type)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Booking Segments */}
                                    {bookingData?.booking_ids.map((booking) => (
                                        <div key={booking.booking_id} className="bg-light-200 border border-light p-3 rounded-2 mb-4">
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div>
                                                    <h6 className="mb-1">Booking #{booking.booking_id}</h6>
                                                    <div className="d-flex gap-3 small text-muted">
                                                        <span>PNR: {booking.pnr}</span>
                                                        <span>Trip Type: {getTripTypeDisplay(booking.trip_type)}</span>
                                                        <span>Status: <span className={`badge ${getStatusBadgeClass(booking.status)}`}>{booking.status}</span></span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Flight Information */}
                                            {getPassengersByBooking(booking.booking_id).map((passenger, paxIndex) => (
                                                <div key={paxIndex} className="mb-3">
                                                    {passenger.Baggage.map((baggage, bagIndex) => (
                                                        <div key={bagIndex} className="d-flex flex-wrap align-items-center justify-content-between">
                                                            <div className="d-flex flex-wrap align-items-center booking-hotels">
                                                                <div className="avatar avatar-lg me-2">
                                                                    <ImageWithBasePath
                                                                        src="assets/img/flight/flight-01.jpg"
                                                                        alt="Flight"
                                                                        className="img-fluid rounded-circle"
                                                                    />
                                                                </div>
                                                                <div className="booking-details">
                                                                    <h6 className="mb-1">
                                                                        {baggage.AirlineCode} {baggage.FlightNumber}
                                                                    </h6>
                                                                    <div className="d-flex flex-wrap align-items-center booking-items">
                                                                        <p className="fs-14 text-gray-6 pe-2 border-end border-light d-flex align-items-center me-2">
                                                                            <ImageWithBasePath
                                                                                src="assets/img/icons/airindia.svg"
                                                                                alt="Airline"
                                                                                className="avatar avatar-sm avatar-rounded me-2"
                                                                            />
                                                                            {baggage.AirlineCode} Airlines
                                                                        </p>
                                                                        <p className="fs-14 text-gray-6 pe-2 border-end border-light d-flex align-items-center me-2">
                                                                            <i className="isax isax-location5 me-1" />
                                                                            {baggage.Origin} → {baggage.Destination}
                                                                        </p>
                                                                        <p className="fs-14 text-gray-6 d-flex align-items-center">
                                                                            <i className="isax isax-bag-2 me-1" />
                                                                            Baggage: {baggage.Weight}kg
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    ))}

                                    {/* Passenger Details */}
                                    <div className="pb-4 border-bottom mb-4">
                                        <h6 className="mb-3">Passenger Details</h6>
                                        <div className="row g-3">
                                            {bookingData?.passengers.map((passenger, index) => (
                                                <div key={index} className="col-lg-6">
                                                    <div className="border rounded p-3">
                                                        <h6 className="mb-2">
                                                            {passenger.Title} {passenger.FirstName} {passenger.LastName}
                                                            {passenger.IsLeadPax && <span className="badge bg-primary ms-2">Lead</span>}
                                                            <span className="badge bg-secondary ms-2">Ticket: #{passenger.TicketId}</span>
                                                        </h6>
                                                        <div className="row small">
                                                            <div className="col-6">
                                                                <span className="text-muted">Type:</span> {getPassengerType(passenger.PaxType)}
                                                            </div>
                                                            <div className="col-6">
                                                                <span className="text-muted">Gender:</span> {getGenderText(passenger.Gender)}
                                                            </div>
                                                            <div className="col-12 mt-1">
                                                                <span className="text-muted">DOB:</span> {formatDate(passenger.DateOfBirth)}
                                                            </div>
                                                            <div className="col-12 mt-1">
                                                                <span className="text-muted">Email:</span> {passenger.Email}
                                                            </div>
                                                            <div className="col-12 mt-1">
                                                                <span className="text-muted">Contact:</span> {passenger.ContactNo}
                                                            </div>
                                                            <div className="col-12 mt-1">
                                                                <span className="text-muted">Nationality:</span> {passenger.Nationality}
                                                            </div>
                                                        </div>

                                                        {/* Seat Information */}
                                                        {passenger.SeatDynamic.map((seat, seatIndex) => (
                                                            seat.SeatNo && (
                                                                <div key={seatIndex} className="mt-2 p-2 bg-light rounded small">
                                                                    <strong>Seat:</strong> {seat.SeatNo} ({seat.RowNo})
                                                                    {seat.Price > 0 && <span className="text-success ms-2">+₹{seat.Price}</span>}
                                                                </div>
                                                            )
                                                        ))}

                                                        {/* Meal Information */}
                                                        {passenger.MealDynamic.map((meal, mealIndex) => (
                                                            meal.Code !== 'NoMeal' && (
                                                                <div key={mealIndex} className="mt-2 p-2 bg-light rounded small">
                                                                    <strong>Meal:</strong> {meal.AirlineDescription || meal.Code}
                                                                    {meal.Price > 0 && <span className="text-success ms-2">+₹{meal.Price}</span>}
                                                                </div>
                                                            )
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Booking Info */}
                                    <div className="pb-4 border-bottom mb-4">
                                        <h6 className="mb-3">Booking Information</h6>
                                        <div className="row g-3">
                                            <div className="col-lg-3">
                                                <h6 className="fs-14">Booking Date</h6>
                                                <p className="text-gray-6 fs-16">{new Date().toLocaleDateString()}</p>
                                            </div>
                                            <div className="col-lg-3">
                                                <h6 className="fs-14">Total Bookings</h6>
                                                <p className="text-gray-6 fs-16">{bookingData?.booking_ids.length || 0}</p>
                                            </div>
                                            <div className="col-lg-3">
                                                <h6 className="fs-14">Email Sent</h6>
                                                <p className={bookingData?.email_sent ? "text-success fs-16" : "text-warning fs-16"}>
                                                    {bookingData?.email_sent ? 'Yes' : 'No'}
                                                </p>
                                            </div>
                                            <div className="col-lg-3">
                                                <h6 className="fs-14">Total Passengers</h6>
                                                <p className="text-gray-6 fs-16">{bookingData?.passengers.length || 0}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Fare Breakdown */}
                                    <div className="pb-4 border-bottom mb-4">
                                        <h6 className="mb-3">Fare Breakdown</h6>
                                        <div className="row g-3">
                                            <div className="col-lg-3">
                                                <h6 className="fs-14">Base Fare</h6>
                                                <p className="text-gray-6 fs-16">₹{totalBaseFare.toLocaleString()}</p>
                                            </div>
                                            <div className="col-lg-3">
                                                <h6 className="fs-14">Taxes & Fees</h6>
                                                <p className="text-gray-6 fs-16">₹{totalTax.toLocaleString()}</p>
                                            </div>
                                            <div className="col-lg-3">
                                                <h6 className="fs-14">Additional Services</h6>
                                                <p className="text-gray-6 fs-16">
                                                    ₹{bookingData?.passengers.reduce((sum, passenger) => {
                                                        const seatCost = passenger.SeatDynamic.reduce((seatSum, seat) => seatSum + seat.Price, 0);
                                                        const mealCost = passenger.MealDynamic.reduce((mealSum, meal) => mealSum + meal.Price, 0);
                                                        const baggageCost = passenger.Baggage.reduce((bagSum, bag) => bagSum + bag.Price, 0);
                                                        return sum + seatCost + mealCost + baggageCost;
                                                    }, 0).toLocaleString()}
                                                </p>
                                            </div>
                                            <div className="col-lg-3">
                                                <h6 className="fs-14">Total Amount</h6>
                                                <p className="text-gray-6 fs-16 fw-bold">₹{totalAmount.toLocaleString()}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Contact Information */}
                                    {bookingData?.lead_passenger && (
                                        <div className="pb-4 border-bottom mb-4">
                                            <h6 className="mb-3">Lead Passenger Contact</h6>
                                            <div className="row g-3">
                                                <div className="col-lg-3">
                                                    <h6 className="fs-14">Name</h6>
                                                    <p className="text-gray-6 fs-16">
                                                        {bookingData.lead_passenger.title} {bookingData.lead_passenger.first_name} {bookingData.lead_passenger.last_name}
                                                    </p>
                                                </div>
                                                <div className="col-lg-3">
                                                    <h6 className="fs-14">Email</h6>
                                                    <p className="text-gray-6 fs-16">{bookingData.lead_passenger.email}</p>
                                                </div>
                                                <div className="col-lg-3">
                                                    <h6 className="fs-14">Phone</h6>
                                                    <p className="text-gray-6 fs-16">{bookingData.lead_passenger.phone}</p>
                                                </div>
                                                <div className="col-lg-3">
                                                    <h6 className="fs-14">Role</h6>
                                                    <p className="text-gray-6 fs-16">Lead Passenger</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Additional Actions */}
                                    <div className="text-center mt-4">
                                        <Link to={routes.flightList} className="btn btn-primary me-3">
                                            Book Another Flight
                                        </Link>
                                        <Link to={routes.home1} className="btn btn-outline-primary">
                                            Back to Home
                                        </Link>
                                        {/* {bookingData?.pdf_path && (
                                            <a
                                                href={bookingData.pdf_path}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-success ms-3"
                                            >
                                                Download Ticket
                                            </a>
                                        )} */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FlightBookingConfirmation;